import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { invalidateFinanceData } from '../../utils/financeQueries';
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Link,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PendingActionsIcon from '@mui/icons-material/PendingActionsOutlined';
import AttachFileIcon from '@mui/icons-material/AttachFileOutlined';
import { listFinanceLedger } from '../../api/finance';
import { useBranchFilter } from '../../context/branchContext';
import { PageHeader } from '../../components/PageHeader';
import { TableToolbar } from '../../components/table/TableToolbar';
import { FinanceFilters, FinanceLedgerRow } from '../../types/finance';
import { ApprovalStatusChip } from './ApprovalStatusChip';
import { FinanceFilterBar } from './FinanceFilterBar';
import { PaymentReviewDialog } from './PaymentReviewDialog';
import { formatDate, formatMoney } from './financeFormat';
import { NumberLabel } from '../../components/NumberLabel';

/** Finance → Payment Confirmations (Phase 96; approval workflow since
 * Phase 97): every payment pending finance approval in the caller's branch
 * — the same queue for a Finance Officer and a Super Admin (scoped to
 * theirs). Click a row (or arrive with `?paymentId=` from the dashboard or
 * a notification) to review it: details, slip, Approve (a receipt is
 * issued) or Reject with a reason. Approving never depends on a slip. */
export const PaymentConfirmationsPage = () => {
  const queryClient = useQueryClient();
  const { selectedBranchId } = useBranchFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const reviewId = Number(searchParams.get('paymentId')) || null;
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 20,
  });
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<FinanceFilters>({});
  const [toast, setToast] = useState<string | null>(null);

  const params = {
    ...filters,
    branchId: selectedBranchId,
    search: search || undefined,
    status: 'PENDING' as const,
  };
  const listQuery = useQuery({
    queryKey: ['finance-confirmations', paginationModel, params],
    queryFn: () =>
      listFinanceLedger({
        ...params,
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      }),
    placeholderData: (prev) => prev,
  });

  const openReview = (id: number) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('paymentId', String(id));
        return next;
      },
      { replace: true },
    );
  const closeReview = () =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('paymentId');
        return next;
      },
      { replace: true },
    );

  const refresh = () => invalidateFinanceData(queryClient);

  const columns: GridColDef<FinanceLedgerRow>[] = [
    { field: 'paymentNumber', headerName: 'Payment', width: 190, renderCell: ({ row }) => <NumberLabel value={row.paymentNumber} kind="payment" /> },
    {
      field: 'student',
      headerName: 'Student',
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: ({ row }) => (
        <Link
          component={RouterLink}
          to={`/finance/students/${row.student.id}?paymentId=${row.id}`}
          onClick={(e) => e.stopPropagation()}
        >
          {row.student.name} ({row.student.studentNumber})
        </Link>
      ),
    },
    {
      field: 'category',
      headerName: 'Payment item',
      width: 190,
      valueGetter: (_v, row) =>
        `${row.category.name}${row.type !== 'PAYMENT' ? ` (${row.type.toLowerCase()})` : ''}${row.resubmittedFrom ? ' — resubmitted' : ''}`,
    },
    {
      field: 'totalWithTax',
      headerName: 'Amount incl. SSCL',
      width: 145,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (_v, row) => formatMoney(row.totalWithTax, row.currency),
    },
    {
      field: 'reference',
      headerName: 'Method / ref.',
      width: 160,
      valueGetter: (_v, row) =>
        [row.paymentMethod?.replace(/_/g, ' ').toLowerCase(), row.referenceNumber]
          .filter(Boolean)
          .join(' · ') || '—',
    },
    {
      field: 'createdAt',
      headerName: 'Submitted',
      width: 110,
      valueFormatter: (v) => formatDate(v),
    },
    { field: 'recordedBy', headerName: 'Submitted by', width: 140, valueFormatter: (v) => v ?? '—' },
    {
      field: 'status',
      headerName: 'Status',
      width: 190,
      renderCell: ({ row }) => <ApprovalStatusChip status={row.status} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 170,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: '100%' }}>
          {row.slip ? (
            <AttachFileIcon fontSize="small" color="action" titleAccess="Slip uploaded" />
          ) : (
            <Box component="span" sx={{ fontSize: 12, color: 'text.secondary', px: 0.5 }}>
              No slip
            </Box>
          )}
          <Button
            size="small"
            variant="outlined"
            onClick={(e) => {
              e.stopPropagation();
              openReview(row.id);
            }}
          >
            Review
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={PendingActionsIcon}
        color="#1565C0"
        title="Payment Confirmations"
        subtitle="Payments pending finance approval. Review each one, then approve (a receipt is issued) or reject with a reason."
      />
      <FinanceFilterBar
        value={filters}
        onChange={(next) => {
          setFilters(next);
          setPaginationModel((p) => ({ ...p, page: 0 }));
        }}
      >
        <TextField
          placeholder="Search payment, reference, student…"
          size="small"
          sx={{ width: 260 }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </FinanceFilterBar>

      {listQuery.data && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          {listQuery.data.meta.total} payment(s) pending finance approval — none of these count
          as paid until approved.
        </Typography>
      )}

      {listQuery.isError ? (
        <Alert severity="error">Couldn't load pending payments.</Alert>
      ) : (
        <Box sx={{ height: 620, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            slots={{ toolbar: TableToolbar }}
            localeText={{ noRowsLabel: 'No payments are pending finance approval.' }}
            disableRowSelectionOnClick
            onRowClick={(p) => openReview(p.row.id)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      )}

      <PaymentReviewDialog
        paymentId={reviewId}
        onClose={closeReview}
        onDecided={(payment, decision) => {
          setToast(
            decision === 'approved'
              ? `${payment.paymentNumber} approved${payment.receipt ? ` — receipt ${payment.receipt.receiptNumber} issued` : ''}.`
              : decision === 'reversed'
                ? `${payment.paymentNumber} reversed — its receipt is void.`
                : decision === 'deleted'
                  ? `${payment.paymentNumber} deleted — kept in the audit history.`
                  : `${payment.paymentNumber} rejected — the counselor has been notified.`,
          );
          refresh();
        }}
      />
      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity="success" onClose={() => setToast(null)}>
            {toast}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
